// ═══════════════════════════════════════════════════════════════════════════
// CONDIVISIONE DI ATTIVITÀ E COMMESSE — regole pure (ottobre 2026)
// ═══════════════════════════════════════════════════════════════════════════
// Decisioni di Paolo (ott 2026):
//  • Un'attività si condivide con colleghi della STESSA azienda (mai clienti né altri).
//  • Chi non ha pieni diritti (vedi dirittiPieni) COLLABORA: vede, commenta, compila i campi
//    dati, aggiunge sottoattività e nuove attività. NON cambia nome dell'attività,
//    numerazione/ordine, stato, scadenza, assegnatario, priorità, descrizione, e non la elimina.
//  • Una commessa si condivide con colleghi: la vedono anche se ha membri, la trovano in
//    "Le mie commesse" e lavorano come i membri operativi.
// Campi: tasks.condivisaCon [uid], tasks.condivisaConNomi {uid: nome}, tasks.storicoCondivisione [];
//        projects.sharedWith [uid], projects.sharedWithNomi {uid: nome}.
// ═══════════════════════════════════════════════════════════════════════════

/** Utenti con cui si può condividere: stessa azienda, attivi, persone vere (no demo, no me). */
export function utentiCondivisibili(utenti = [], { mioUid = "", orgId = "" } = {}) {
  return utenti.filter(u => u && u.uid && u.uid !== mioUid && u.enabled !== false &&
    (!orgId || u.organizationId === orgId) && !["cliente", "demo", "visitatore"].includes(u.role))
    .sort((a, b) => (a.displayName || "").localeCompare(b.displayName || "", "it"));
}

/**
 * Pieni diritti su un'attività (modello definito ott 2026, "il più logico e professionale"):
 *  • amministratori, titolare, Platform Owner e ruolo operativo "Gestione aziendale";
 *  • responsabile della commessa e membri della commessa con ruolo "Responsabile";
 *  • chi ha creato l'attività e chi ne è assegnatario.
 * Tutti gli altri colleghi COLLABORANO: vedono, commentano, compilano i campi dati,
 * aggiungono sottoattività (e modificano/eliminano solo le proprie) e nuove attività;
 * nome, stato, scadenza, assegnatario, priorità, descrizione ed eliminazione restano a chi ha
 * pieni diritti. La condivisione dà visibilità e avvisi, non diritti in più.
 */
export function dirittiPieni(task, { uid, isAdmin = false, ruoloOperativo = "", responsabileCommessaUid = "", membriCommessa = [] } = {}) {
  if (!task || !uid) return false;
  if (isAdmin || ruoloOperativo === "gestione") return true;
  if (task.assignedTo === uid || task.createdBy === uid) return true;
  if (responsabileCommessaUid && responsabileCommessaUid === uid) return true;
  return (membriCommessa || []).some(m => m && m.uid === uid && m.ruolo === "responsabile");
}

/**
 * Come vede l'attività questo utente:
 *  "pieno"       → può modificare tutto;
 *  "collabora"   → collega (anche con attività o commessa condivisa): collabora soltanto.
 */
export function ruoloSuAttivita(task, opzioni = {}) {
  return dirittiPieni(task, opzioni) ? "pieno" : "collabora";
}

/** Può modificare/eliminare questa sottoattività? (chi ha pieni diritti sull'attività, o chi l'ha creata/ne è assegnatario) */
export function puoGestireSottoattivita(subtask, ruoloAttivita, uid) {
  if (ruoloAttivita === "pieno") return true;
  return !!uid && (subtask?.createdBy === uid || subtask?.assignedTo === uid);
}

/** Può condividere la commessa con altri colleghi? (amministratori, Gestione aziendale, responsabili della commessa) */
export function puoCondividereCommessa(project, { uid, isAdmin = false, ruoloOperativo = "" } = {}) {
  if (isAdmin || ruoloOperativo === "gestione") return true;
  if (!project || !uid) return false;
  if (project.responsabileUid === uid) return true;
  return (project.membri || []).some(m => m && m.uid === uid && m.ruolo === "responsabile");
}

/** Ruolo dell'utente nella commessa: "responsabile" | "operativo" (membri) | "condiviso" | null. */
export function ruoloInCommessa(project, uid) {
  if (!project || !uid) return null;
  const m = (project.membri || []).find(x => x && x.uid === uid);
  if (m) return m.ruolo || "operativo";
  if (project.responsabileUid === uid) return "responsabile";
  return (project.sharedWith || []).includes(uid) ? "condiviso" : null;
}

/** Destinatari degli avvisi di un'attività: assegnatario, creatore, persone con cui è condivisa (escluso chi agisce). */
export function destinatariAttivita(task, escluso = "") {
  const s = new Set([task?.assignedTo, task?.createdBy, ...(task?.condivisaCon || [])].filter(Boolean));
  s.delete(escluso);
  return [...s];
}

/** Differenza tra due elenchi di persone: chi è stato aggiunto e chi tolto. */
export function differenzaPersone(prima = [], dopo = []) {
  return { aggiunti: dopo.filter(u => !prima.includes(u)), tolti: prima.filter(u => !dopo.includes(u)) };
}

/** La commessa è visibile a questo utente nell'elenco? (membri o condivisione; amministratori sempre) */
export function commessaVisibile(p, { uid, isAdmin = false } = {}) {
  if (!p || isAdmin || p.isParent) return true;
  if ((p.sharedWith || []).includes(uid)) return true;
  if (p.hasMembri && (p.membri || []).length > 0) return (p.membri || []).some(m => m.uid === uid) || p.responsabileUid === uid;
  return true;
}
